import {
  GetAccessTokenSilentlyType,
  GetAccessTokenWithPopupType
} from '@/types/transcriptionSessionTypes';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';
const CUSTOMER_AGENT_ENDPOINT = `${API_BASE_URL}/customeragent`;

export interface CompanySmsEndpoint {
  id: string;
  companyId: string;
  phoneNumber: string;
  normalizedPhoneNumber: string;
  displayName?: string | null;
  description?: string | null;
  isPrimary: boolean;
  isActive: boolean;
  twilioPhoneNumberSid?: string | null;
  endpointType: number;
  linkedDealerIds: string[];
  linkedDealerNames: string[];
}

export interface UpsertCompanySmsEndpointInput {
  id?: string | null;
  phoneNumber: string;
  displayName?: string | null;
  description?: string | null;
  isPrimary: boolean;
  isActive: boolean;
  twilioPhoneNumberSid?: string | null;
  linkedDealerIds: string[];
  endpointType: number;
}

export interface CustomerAgentDealer {
  id: string;
  companyId: string;
  name: string;
  normalizedName: string;
  description?: string | null;
  isActive: boolean;
  isDefaultOnboardingDealer: boolean;
  linkedEndpointIds: string[];
  linkedEndpointPhoneNumbers: string[];
}

export interface UpsertCustomerAgentDealerInput {
  id?: string | null;
  name: string;
  description?: string | null;
  isActive: boolean;
  isDefaultOnboardingDealer: boolean;
}

export interface CustomerAgentAuthorizedContact {
  id: string;
  companyId: string;
  dealerId: string;
  dealerName?: string | null;
  phoneNumber: string;
  normalizedPhoneNumber: string;
  displayName?: string | null;
  description?: string | null;
  isActive: boolean;
  revokedAt?: string | null;
  consentStatus: number;
  consentSource: number;
  consentPromptVersion?: string | null;
  accessExpiresAt?: string | null;
  consentedAt?: string | null;
  optedOutAt?: string | null;
  enrollmentCodeId?: string | null;
  firstInboundMessageAt?: string | null;
  lastInboundMessageAt?: string | null;
}

export interface UpsertCustomerAgentAuthorizedContactInput {
  id?: string | null;
  dealerId: string;
  phoneNumber: string;
  displayName?: string | null;
  description?: string | null;
  accessExpiresAt?: string | null;
}

export interface CustomerAgentEnrollmentCode {
  id: string;
  companyId: string;
  dealerId: string;
  dealerName?: string | null;
  code: string;
  normalizedCode: string;
  displayName?: string | null;
  isActive: boolean;
  expiresAt?: string | null;
  maxUses?: number | null;
  useCount: number;
  lastUsedAt?: string | null;
}

export interface UpsertCustomerAgentEnrollmentCodeInput {
  id?: string | null;
  dealerId: string;
  code: string;
  displayName?: string | null;
  isActive: boolean;
  expiresAt?: string | null;
  maxUses?: number | null;
}

const getAuthToken = async (
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): Promise<string> => {
  const authParams = {
    authorizationParams: {
      audience: import.meta.env.VITE_API_AUTH0_AUDIENCE
    }
  };

  try {
    return await getAccessTokenSilently(authParams);
  } catch (error: any) {
    if (
      error.error === 'consent_required' ||
      error.error === 'login_required' ||
      error.error === 'interaction_required'
    ) {
      const token = await getAccessTokenWithPopup(authParams);
      if (!token) {
        throw new Error('Interactive token acquisition failed.');
      }
      return token;
    }
    throw error;
  }
};

const parseError = async (response: Response): Promise<string> => {
  const payload = await response.json().catch(() => null);
  if (payload && typeof payload.message === 'string') {
    return payload.message;
  }
  if (typeof payload === 'string') {
    return payload;
  }
  return `${response.status} ${response.statusText}`;
};

const requestJson = async <T>(
  url: string,
  userId: string,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType,
  options: RequestInit = {}
): Promise<T> => {
  const token = await getAuthToken(getAccessTokenSilently, getAccessTokenWithPopup);
  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      'X-User-Id': userId,
      ...options.headers
    }
  });

  if (!response.ok) {
    throw new Error(await parseError(response));
  }

  return (await response.json()) as T;
};

export const getCompanySmsEndpoints = async (
  companyId: string,
  userId: string,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): Promise<CompanySmsEndpoint[]> => {
  return requestJson<CompanySmsEndpoint[]>(
    `${CUSTOMER_AGENT_ENDPOINT}/company/${companyId}/sms-endpoints`,
    userId,
    getAccessTokenSilently,
    getAccessTokenWithPopup
  );
};

export const getCustomerAgentDealers = async (
  companyId: string,
  userId: string,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): Promise<CustomerAgentDealer[]> => {
  return requestJson<CustomerAgentDealer[]>(
    `${CUSTOMER_AGENT_ENDPOINT}/company/${companyId}/dealers`,
    userId,
    getAccessTokenSilently,
    getAccessTokenWithPopup
  );
};

export const upsertCustomerAgentDealer = async (
  companyId: string,
  input: UpsertCustomerAgentDealerInput,
  userId: string,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): Promise<CustomerAgentDealer> => {
  return requestJson<CustomerAgentDealer>(
    `${CUSTOMER_AGENT_ENDPOINT}/company/${companyId}/dealers`,
    userId,
    getAccessTokenSilently,
    getAccessTokenWithPopup,
    {
      method: 'POST',
      body: JSON.stringify(input)
    }
  );
};

export const revokeCustomerAgentDealer = async (
  companyId: string,
  dealerId: string,
  userId: string,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): Promise<CustomerAgentDealer> => {
  return requestJson<CustomerAgentDealer>(
    `${CUSTOMER_AGENT_ENDPOINT}/company/${companyId}/dealers/${dealerId}`,
    userId,
    getAccessTokenSilently,
    getAccessTokenWithPopup,
    { method: 'DELETE' }
  );
};

export const upsertCompanySmsEndpoint = async (
  companyId: string,
  input: UpsertCompanySmsEndpointInput,
  userId: string,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): Promise<CompanySmsEndpoint> => {
  return requestJson<CompanySmsEndpoint>(
    `${CUSTOMER_AGENT_ENDPOINT}/company/${companyId}/sms-endpoints`,
    userId,
    getAccessTokenSilently,
    getAccessTokenWithPopup,
    {
      method: 'POST',
      body: JSON.stringify(input)
    }
  );
};

export const revokeCompanySmsEndpoint = async (
  companyId: string,
  endpointId: string,
  userId: string,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): Promise<CompanySmsEndpoint> => {
  return requestJson<CompanySmsEndpoint>(
    `${CUSTOMER_AGENT_ENDPOINT}/company/${companyId}/sms-endpoints/${endpointId}`,
    userId,
    getAccessTokenSilently,
    getAccessTokenWithPopup,
    { method: 'DELETE' }
  );
};

export const getCustomerAgentEnrollmentCodes = async (
  companyId: string,
  userId: string,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): Promise<CustomerAgentEnrollmentCode[]> => {
  return requestJson<CustomerAgentEnrollmentCode[]>(
    `${CUSTOMER_AGENT_ENDPOINT}/company/${companyId}/enrollment-codes`,
    userId,
    getAccessTokenSilently,
    getAccessTokenWithPopup
  );
};

export const upsertCustomerAgentEnrollmentCode = async (
  companyId: string,
  input: UpsertCustomerAgentEnrollmentCodeInput,
  userId: string,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): Promise<CustomerAgentEnrollmentCode> => {
  return requestJson<CustomerAgentEnrollmentCode>(
    `${CUSTOMER_AGENT_ENDPOINT}/company/${companyId}/enrollment-codes`,
    userId,
    getAccessTokenSilently,
    getAccessTokenWithPopup,
    {
      method: 'POST',
      body: JSON.stringify(input)
    }
  );
};

export const revokeCustomerAgentEnrollmentCode = async (
  companyId: string,
  enrollmentCodeId: string,
  userId: string,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): Promise<CustomerAgentEnrollmentCode> => {
  return requestJson<CustomerAgentEnrollmentCode>(
    `${CUSTOMER_AGENT_ENDPOINT}/company/${companyId}/enrollment-codes/${enrollmentCodeId}`,
    userId,
    getAccessTokenSilently,
    getAccessTokenWithPopup,
    { method: 'DELETE' }
  );
};

export const getCustomerAgentAuthorizedContacts = async (
  companyId: string,
  userId: string,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): Promise<CustomerAgentAuthorizedContact[]> => {
  return requestJson<CustomerAgentAuthorizedContact[]>(
    `${CUSTOMER_AGENT_ENDPOINT}/company/${companyId}/authorized-contacts`,
    userId,
    getAccessTokenSilently,
    getAccessTokenWithPopup
  );
};

export const upsertCustomerAgentAuthorizedContact = async (
  companyId: string,
  input: UpsertCustomerAgentAuthorizedContactInput,
  userId: string,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): Promise<CustomerAgentAuthorizedContact> => {
  return requestJson<CustomerAgentAuthorizedContact>(
    `${CUSTOMER_AGENT_ENDPOINT}/company/${companyId}/authorized-contacts`,
    userId,
    getAccessTokenSilently,
    getAccessTokenWithPopup,
    {
      method: 'POST',
      body: JSON.stringify(input)
    }
  );
};

export const revokeCustomerAgentAuthorizedContact = async (
  companyId: string,
  contactId: string,
  userId: string,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): Promise<CustomerAgentAuthorizedContact> => {
  return requestJson<CustomerAgentAuthorizedContact>(
    `${CUSTOMER_AGENT_ENDPOINT}/company/${companyId}/authorized-contacts/${contactId}`,
    userId,
    getAccessTokenSilently,
    getAccessTokenWithPopup,
    { method: 'DELETE' }
  );
};
